import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import api from '../lib/api';
import { getAuth } from '../lib/auth';
import { MainLayout } from '../components/MainLayout';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { FileText, Upload, Trash2, Eye, Loader2, RefreshCw, Search, X, CheckCircle, Clock, AlertCircle, AlertTriangle, Mail, UploadCloud, Layers, FileStack, ListTodo, Download, MoreVertical, ArrowRight, History, Webhook, CheckCheck, Shield, Copy, Zap, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';

export const BillJobs = () => {
  const navigate = useNavigate();
  const { user } = getAuth();
  const currentAccountStr = localStorage.getItem('current_account');
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, filename: '' });
  const [loading, setLoading] = useState(true);
  
  // Bill type tab state
  const [activeBillType, setActiveBillType] = useState('single');
  const [showDuplicates, setShowDuplicates] = useState(false);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [jobIdSearch, setJobIdSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [commodityFilter, setCommodityFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Account users for assignment
  const [accountUsers, setAccountUsers] = useState([]);
  
  // Bulk reprocess state
  const [bulkReprocessing, setBulkReprocessing] = useState(false);
  const [selectedBills, setSelectedBills] = useState([]);
  
  // Job queue bills (queued, processing, failed)
  const queueBills = useMemo(() => {
    return documents.filter(doc => 
      doc.status === 'uploaded' || 
      doc.status === 'processing' || 
      doc.status === 'failed' ||
      doc.status === 'queued'
    );
  }, [documents]);
  
  // Separate single and consolidated bills (only completed ones)
  const singleBills = useMemo(() => {
    return documents.filter(doc => doc.status === 'completed' && doc.bill_type !== 'consolidated');
  }, [documents]);
  
  const consolidatedBills = useMemo(() => {
    return documents.filter(doc => doc.status === 'completed' && doc.bill_type === 'consolidated');
  }, [documents]);
  
  // Filtered documents based on active tab
  const filteredDocuments = useMemo(() => {
    let baseDocs;
    if (activeBillType === 'queue') {
      baseDocs = queueBills;
    } else if (activeBillType === 'consolidated') {
      baseDocs = consolidatedBills;
    } else {
      baseDocs = singleBills;
    }
    
    return baseDocs.filter(doc => {
      // Hide duplicates by default unless showDuplicates is true
      if (!showDuplicates && doc.is_duplicate && activeBillType !== 'queue') return false;
      
      // Job ID search - exact or partial match (searches both document ID and external_id/bill_track_id)
      if (jobIdSearch) {
        const jobId = jobIdSearch.trim().toLowerCase();
        const docId = (doc.id || '').toLowerCase();
        const externalId = (doc.external_id || '').toLowerCase();
        if (!docId.includes(jobId) && !externalId.includes(jobId)) return false;
      }
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesFilename = doc.filename?.toLowerCase().includes(query);
        const matchesVendor = doc.vendor_name?.toLowerCase().includes(query);
        if (!matchesFilename && !matchesVendor) return false;
      }
      
      if (statusFilter !== 'all' && doc.status !== statusFilter) return false;
      if (commodityFilter !== 'all' && doc.commodity !== commodityFilter) return false;
      
      if (assignedFilter === 'unassigned' && doc.assigned_to) return false;
      if (assignedFilter === 'me' && doc.assigned_to !== user?.id) return false;
      if (assignedFilter === 'others' && (!doc.assigned_to || doc.assigned_to === user?.id)) return false;
      
      if (dateFrom) {
        const docDate = new Date(doc.uploaded_at);
        const fromDate = new Date(dateFrom);
        if (docDate < fromDate) return false;
      }
      if (dateTo) {
        const docDate = new Date(doc.uploaded_at);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (docDate > toDate) return false;
      }
      
      return true;
    });
  }, [queueBills, singleBills, consolidatedBills, activeBillType, searchQuery, jobIdSearch, statusFilter, commodityFilter, assignedFilter, dateFrom, dateTo, user?.id, showDuplicates]);
  
  // Count of hidden duplicates
  const duplicateCount = useMemo(() => {
    if (activeBillType === 'queue') return 0;
    const baseDocs = activeBillType === 'consolidated' ? consolidatedBills : singleBills;
    return baseDocs.filter(doc => doc.is_duplicate).length;
  }, [activeBillType, consolidatedBills, singleBills]);
  
  const clearFilters = () => {
    setSearchQuery('');
    setJobIdSearch('');
    setStatusFilter('all');
    setCommodityFilter('all');
    setAssignedFilter('all');
    setDateFrom('');
    setDateTo('');
  };
  
  const hasActiveFilters = searchQuery || jobIdSearch || statusFilter !== 'all' || commodityFilter !== 'all' || assignedFilter !== 'all' || dateFrom || dateTo;

  useEffect(() => {
    if (!currentAccount) {
      toast.error('No account selected');
      return;
    }
    fetchDocuments();
    fetchAccountUsers();
    const interval = setInterval(fetchDocuments, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount?.id]);

  const fetchDocuments = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/documents`);
      setDocuments(data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountUsers = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/users-list`);
      setAccountUsers(data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  // Bulk reprocess all bills in queue
  const handleBulkReprocessQueue = async () => {
    if (!currentAccount) return;
    
    const queuedIds = queueBills.filter(d => d.status !== 'processing').map(d => d.id);
    if (queuedIds.length === 0) {
      toast.info('No bills to reprocess in queue');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to reprocess all ${queuedIds.length} bills in the queue?`)) return;
    
    setBulkReprocessing(true);
    try {
      const { data } = await api.post(`/accounts/${currentAccount.id}/bulk-reprocess`, {
        document_ids: queuedIds
      });
      toast.success(`Sent ${data.queued} bills for reprocessing`);
      setSelectedBills([]);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reprocess');
    } finally {
      setBulkReprocessing(false);
    }
  };

  // Bulk reprocess selected bills
  const handleBulkReprocessSelected = async () => {
    if (!currentAccount || selectedBills.length === 0) return;
    
    setBulkReprocessing(true);
    try {
      const { data } = await api.post(`/accounts/${currentAccount.id}/bulk-reprocess`, {
        document_ids: selectedBills
      });
      toast.success(`Sent ${data.queued} bills for reprocessing`);
      setSelectedBills([]);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reprocess');
    } finally {
      setBulkReprocessing(false);
    }
  };

  // Bulk reprocess all failed bills
  const handleBulkReprocessFailed = async () => {
    if (!currentAccount) return;
    
    const failedIds = allDocs.filter(d => d.status === 'failed').map(d => d.id);
    if (failedIds.length === 0) {
      toast.info('No failed bills to reprocess');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to reprocess all ${failedIds.length} failed bills?`)) return;
    
    setBulkReprocessing(true);
    try {
      const { data } = await api.post(`/accounts/${currentAccount.id}/bulk-reprocess`, {
        document_ids: failedIds
      });
      toast.success(`Sent ${data.queued} failed bills for reprocessing`);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reprocess');
    } finally {
      setBulkReprocessing(false);
    }
  };

  // Bulk reprocess low accuracy bills (<50%)
  const handleBulkReprocessLowAccuracy = async () => {
    if (!currentAccount) return;
    
    const lowAccuracyIds = allDocs.filter(d => 
      d.status === 'completed' && 
      d.confidence_score !== undefined && 
      d.confidence_score < 0.5
    ).map(d => d.id);
    
    if (lowAccuracyIds.length === 0) {
      toast.info('No low accuracy bills to reprocess');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to reprocess all ${lowAccuracyIds.length} low accuracy bills?`)) return;
    
    setBulkReprocessing(true);
    try {
      const { data } = await api.post(`/accounts/${currentAccount.id}/bulk-reprocess`, {
        document_ids: lowAccuracyIds
      });
      toast.success(`Sent ${data.queued} low accuracy bills for reprocessing`);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk reprocess');
    } finally {
      setBulkReprocessing(false);
    }
  };

  // Toggle bill selection
  const toggleBillSelection = (docId, e) => {
    e?.stopPropagation?.();
    setSelectedBills(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // Select all bills in queue
  const selectAllQueueBills = () => {
    const queuedIds = queueBills.filter(d => d.status !== 'processing').map(d => d.id);
    setSelectedBills(queuedIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedBills([]);
  };

  // Copy to clipboard helper
  const copyToClipboard = (text, label = 'ID') => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const handleAssign = async (docId, userId, e) => {
    e?.stopPropagation?.();
    try {
      await api.patch(`/accounts/${currentAccount.id}/documents/${docId}/assign`, {
        user_id: userId === '__unassigned__' ? null : userId
      });
      const assignedUser = accountUsers.find(u => u.id === userId);
      toast.success(userId === '__unassigned__' ? 'Assignment removed' : `Assigned to ${assignedUser?.name}`);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to assign');
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (!acceptedFiles.length || !currentAccount) return;

    const MAX_FILE_SIZE = 50 * 1024 * 1024;
    const pdfFiles = acceptedFiles.filter(file => {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error(`${file.name} is not a PDF file`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds 50MB limit`);
        return false;
      }
      return true;
    });

    if (pdfFiles.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: pdfFiles.length, filename: '' });
    
    let successCount = 0;
    let failCount = 0;
    const uploadedDocs = [];

    for (let i = 0; i < pdfFiles.length; i++) {
      const file = pdfFiles[i];
      setUploadProgress({ current: i + 1, total: pdfFiles.length, filename: file.name });
      
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post(`/accounts/${currentAccount.id}/documents/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        successCount++;
        
        // Collect uploaded document for optimistic UI update (skip duplicates)
        if (response.data && response.data.id && !response.data.duplicate) {
          uploadedDocs.push({
            ...response.data,
            account_id: currentAccount.id
          });
        } else if (response.data?.duplicate) {
          toast.info(`${file.name} was already uploaded recently`);
        }
      } catch (error) {
        failCount++;
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    setUploading(false);
    setUploadProgress({ current: 0, total: 0, filename: '' });
    
    // Optimistically add uploaded documents to the list immediately
    if (uploadedDocs.length > 0) {
      setDocuments(prev => [...uploadedDocs, ...prev]);
      // Switch to queue tab to show the uploaded files
      setActiveBillType('queue');
    }
    
    if (successCount > 0) {
      toast.success(`Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}`);
    }
    if (failCount > 0) {
      toast.error(`Failed to upload ${failCount} file${failCount > 1 ? 's' : ''}`);
    }
    
    // Fetch fresh data after a short delay to get updated statuses
    setTimeout(() => fetchDocuments(), 1000);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 50,
    disabled: uploading || !currentAccount || currentAccount.role === 'viewer',
  });

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.delete(`/accounts/${currentAccount.id}/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleReparse = async (docId) => {
    try {
      await api.post(`/accounts/${currentAccount.id}/documents/${docId}/reprocess`);
      toast.success('Re-parsing started. Document will update shortly...');
      // Immediately refresh to show "processing" status
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to start re-parsing');
    }
  };

  const handleMoveToBills = async (docId, billType) => {
    try {
      await api.post(`/accounts/${currentAccount.id}/documents/${docId}/move-to-bills`, {
        bill_type: billType
      });
      toast.success(`Document moved to ${billType === 'consolidated' ? 'Consolidated' : 'Single'} Bills`);
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to move document');
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      // First get file info (type and URL)
      const response = await api.get(`/accounts/${currentAccount.id}/documents/${docId}/file`);
      
      let blob;
      if (response.data.type === 's3') {
        // Fetch from S3 presigned URL directly
        const s3Response = await fetch(response.data.url);
        blob = await s3Response.blob();
      } else {
        // Local file - use the download endpoint
        const blobResponse = await api.get(`/accounts/${currentAccount.id}/documents/${docId}/file/download`, {
          responseType: 'blob'
        });
        blob = new Blob([blobResponse.data]);
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'document.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const canUpload = currentAccount && ['admin', 'editor'].includes(currentAccount.role);
  const canDelete = currentAccount && ['admin', 'editor'].includes(currentAccount.role);

  return (
    <MainLayout>
      <div className="grid grid-cols-1 gap-6">
        {/* Upload Area in Accordion */}
        {canUpload && (
          <Accordion type="single" collapsible className="border border-border rounded-none">
            <AccordionItem value="upload" className="border-none">
              <AccordionTrigger className="px-6 py-3 hover:no-underline">
                <div className="flex items-center gap-2">
                  <Upload className="h-5 w-5" strokeWidth={1.5} />
                  <span className="font-bold text-lg">Upload New Bill</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="px-6 pb-6">
                  {uploading ? (
                    <div className="border-2 border-dashed border-primary bg-primary/5 rounded-none p-12 text-center" data-testid="upload-progress">
                      <Loader2 className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" />
                      <h3 className="text-lg font-bold tracking-tight mb-2">Uploading Files...</h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        Processing: <span className="font-mono font-medium">{uploadProgress.filename}</span>
                      </p>
                      <div className="max-w-xs mx-auto">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-mono font-medium">{uploadProgress.current} / {uploadProgress.total}</span>
                        </div>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      {...getRootProps()}
                      data-testid="upload-dropzone"
                      className={`border-2 border-dashed border-border rounded-none p-12 text-center cursor-pointer transition-colors ${
                        isDragActive ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                      }`}
                    >
                      <input {...getInputProps()} />
                      <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1.5} />
                      <h3 className="text-lg font-bold tracking-tight mb-2">
                        {isDragActive ? 'Drop PDFs here' : 'Upload PDF Bills'}
                      </h3>
                      <p className="text-sm text-muted-foreground uppercase tracking-wide">
                        Drag & drop or click to browse (up to 50 files, 50MB each)
                      </p>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="border border-border rounded-none p-4 bg-card">
            <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">Job Queue</p>
            <p className="text-2xl font-black tracking-tighter text-orange-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {queueBills.length}
            </p>
          </div>
          <div className="border border-border rounded-none p-4 bg-card">
            <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">Single Bills</p>
            <p className="text-2xl font-black tracking-tighter text-blue-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {singleBills.length}
            </p>
          </div>
          <div className="border border-border rounded-none p-4 bg-card">
            <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">Consolidated</p>
            <p className="text-2xl font-black tracking-tighter text-purple-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {consolidatedBills.length}
            </p>
          </div>
          <div className="border border-border rounded-none p-4 bg-card">
            <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">Pending Review</p>
            <p className="text-2xl font-black tracking-tighter text-yellow-600" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {documents.filter(d => d.status === 'completed' && d.review_status !== 'approved').length}
            </p>
          </div>
          <div className="border border-border rounded-none p-4 bg-card">
            <p className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1">Total Bills</p>
            <p className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {documents.length}
            </p>
          </div>
        </div>

        {/* Documents Section with Tabs */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold tracking-tight">Intake Hub</h2>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                <X className="h-4 w-4 mr-1" /> Clear filters
              </Button>
            )}
          </div>
          
          {/* Bill Type Tabs */}
          <Tabs value={activeBillType} onValueChange={setActiveBillType} className="mb-6">
            <TabsList className="rounded-none h-10 p-1 bg-muted/50">
              <TabsTrigger 
                value="queue" 
                className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-6"
                data-testid="job-queue-tab"
              >
                <ListTodo className="h-4 w-4 mr-2" />
                Job Queue
                <Badge variant={queueBills.length > 0 ? "default" : "secondary"} className={`ml-2 rounded-none text-xs ${queueBills.length > 0 ? 'bg-orange-500' : ''}`}>{queueBills.length}</Badge>
              </TabsTrigger>
              <TabsTrigger 
                value="single" 
                className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-6"
                data-testid="single-bills-tab"
              >
                <FileText className="h-4 w-4 mr-2" />
                Single Bills
                <Badge variant="secondary" className="ml-2 rounded-none text-xs">{singleBills.length}</Badge>
              </TabsTrigger>
              <TabsTrigger 
                value="consolidated" 
                className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-6"
                data-testid="consolidated-bills-tab"
              >
                <Layers className="h-4 w-4 mr-2" />
                Consolidated Bills
                <Badge variant="secondary" className="ml-2 rounded-none text-xs">{consolidatedBills.length}</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* Bulk Reprocess Bar - Processed Bills Tabs Only (not Queue) */}
          {activeBillType !== 'queue' && (
            <div className="flex items-center justify-between mb-4 p-3 bg-amber-50 border border-amber-200 rounded-none">
              <div className="flex items-center gap-3">
                <Zap className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Reprocess Bills</p>
                  <p className="text-xs text-amber-700">
                    {selectedBills.length > 0 
                      ? `${selectedBills.length} bills selected for reprocessing`
                      : `Select bills to reprocess or reprocess all failed/low-accuracy bills`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {selectedBills.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-none text-amber-700"
                    onClick={clearSelection}
                    data-testid="clear-selection-btn"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      className="rounded-none bg-amber-600 hover:bg-amber-700"
                      disabled={bulkReprocessing}
                      data-testid="reprocess-dropdown-btn"
                    >
                      {bulkReprocessing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      Reprocess
                      <ChevronDown className="h-4 w-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      onClick={handleBulkReprocessSelected}
                      disabled={selectedBills.length === 0}
                      data-testid="reprocess-selected-btn"
                    >
                      <CheckCheck className="h-4 w-4 mr-2" />
                      Selected Bills ({selectedBills.length})
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleBulkReprocessFailed}
                      data-testid="reprocess-failed-btn"
                    >
                      <AlertCircle className="h-4 w-4 mr-2" />
                      All Failed Bills
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={handleBulkReprocessLowAccuracy}
                      data-testid="reprocess-low-accuracy-btn"
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Low Accuracy Bills ({"<"}50%)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-8 gap-3 mb-6">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by filename or vendor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 rounded-none"
                data-testid="search-input"
              />
            </div>
            <div className="relative">
              <Input
                placeholder="Search by Job ID or Bill Track ID..."
                value={jobIdSearch}
                onChange={(e) => setJobIdSearch(e.target.value)}
                className="rounded-none font-mono text-sm"
                data-testid="job-id-search"
              />
              {jobIdSearch && (
                <button
                  onClick={() => setJobIdSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="rounded-none" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="uploaded">Uploaded</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={commodityFilter} onValueChange={setCommodityFilter}>
              <SelectTrigger className="rounded-none" data-testid="commodity-filter">
                <SelectValue placeholder="Commodity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Commodities</SelectItem>
                <SelectItem value="Electricity">Electricity</SelectItem>
                <SelectItem value="Gas">Gas</SelectItem>
                <SelectItem value="Water">Water</SelectItem>
                <SelectItem value="Waste">Waste</SelectItem>
                <SelectItem value="Sewer">Sewer</SelectItem>
                <SelectItem value="Heating Oil">Heating Oil</SelectItem>
              </SelectContent>
            </Select>
            <Select value={assignedFilter} onValueChange={setAssignedFilter}>
              <SelectTrigger className="rounded-none" data-testid="assigned-filter">
                <SelectValue placeholder="Assigned To" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignments</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                <SelectItem value="me">Assigned to Me</SelectItem>
                <SelectItem value="others">Assigned to Others</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              placeholder="From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-none"
              data-testid="date-from"
            />
            <Input
              type="date"
              placeholder="To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-none"
              data-testid="date-to"
            />
            {/* Show Duplicates Toggle */}
            {activeBillType !== 'queue' && duplicateCount > 0 && (
              <Button
                variant={showDuplicates ? "default" : "outline"}
                size="sm"
                className="rounded-none whitespace-nowrap"
                onClick={() => setShowDuplicates(!showDuplicates)}
                data-testid="show-duplicates-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                {showDuplicates ? 'Hide' : 'Show'} Duplicates ({duplicateCount})
              </Button>
            )}
          </div>
          
          
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="border border-border rounded-none p-12 text-center">
              {activeBillType === 'queue' ? (
                <ListTodo className="h-12 w-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1.5} />
              ) : activeBillType === 'consolidated' ? (
                <Layers className="h-12 w-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1.5} />
              ) : (
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1.5} />
              )}
              <p className="text-muted-foreground">
                {documents.length === 0 
                  ? 'No documents uploaded yet' 
                  : activeBillType === 'queue'
                    ? 'No jobs in queue - all bills have been processed!'
                    : activeBillType === 'consolidated'
                      ? 'No consolidated bills found'
                      : 'No single bills match your filters'}
              </p>
            </div>
          ) : (
            <div className="border border-border rounded-none overflow-hidden">
              {hasActiveFilters && (
                <div className="bg-muted/30 px-4 py-2 text-sm text-muted-foreground border-b border-border">
                  Showing {filteredDocuments.length} of {
                    activeBillType === 'queue' ? queueBills.length :
                    activeBillType === 'consolidated' ? consolidatedBills.length : singleBills.length
                  } {activeBillType === 'queue' ? 'jobs' : activeBillType + ' bills'}
                </div>
              )}
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr className="border-b border-border">
                    {activeBillType === 'queue' ? (
                      <>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground w-8"></th>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground w-[120px]">Job ID</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground">Document</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground hidden md:table-cell">Created By</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground">Status</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground">Actions</th>
                      </>
                    ) : (
                      <>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground w-8">
                          <input
                            type="checkbox"
                            className="rounded-none"
                            checked={selectedBills.length > 0 && selectedBills.length === filteredDocuments.length}
                            onChange={(e) => e.target.checked ? setSelectedBills(filteredDocuments.map(d => d.id)) : clearSelection()}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </th>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground w-8"></th>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground w-[120px]">Job ID</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground">Document</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground hidden md:table-cell">Details</th>
                        {activeBillType === 'consolidated' && (
                          <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground">Accounts</th>
                        )}
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground">Status</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground hidden lg:table-cell">Assigned</th>
                        <th className="text-left p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground hidden md:table-cell">Last Update</th>
                        <th className="text-right p-3 text-xs uppercase tracking-wide font-medium text-muted-foreground">Actions</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr
                      key={doc.id}
                      data-testid={`document-${doc.id}`}
                      className="border-b border-border hover:bg-muted/25 transition-colors cursor-pointer"
                      onClick={() => {
                        if (doc.status === 'completed' || doc.status === 'failed') {
                          if (doc.bill_type === 'consolidated') {
                            navigate(`/consolidated-viewer/${doc.id}`);
                          } else {
                            navigate(`/viewer/${doc.id}`);
                          }
                        }
                      }}
                    >
                      {/* Checkbox - Only for Processed Bills (not Queue) */}
                      {activeBillType !== 'queue' && (
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="rounded-none"
                            checked={selectedBills.includes(doc.id)}
                            onChange={(e) => toggleBillSelection(doc.id, e)}
                            disabled={doc.status === 'processing'}
                          />
                        </td>
                      )}
                      <td className="p-3">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              {doc.source === 'emailed' ? (
                                <Mail className="h-4 w-4 text-blue-500" />
                              ) : doc.source === 'api' ? (
                                <Webhook className="h-4 w-4 text-purple-500" />
                              ) : (
                                <UploadCloud className="h-4 w-4 text-muted-foreground" />
                              )}
                            </TooltipTrigger>
                            <TooltipContent>
                              {doc.source === 'emailed' ? 'Received via email' : doc.source === 'api' ? 'Uploaded via API' : 'Uploaded manually'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                      
                      {/* Job ID Column */}
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="font-mono text-[10px] text-muted-foreground cursor-pointer hover:text-foreground">
                                  {doc.id.substring(0, 8)}...
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="font-mono text-xs">{doc.id}</p>
                                {doc.parent_id && doc.parent_id !== doc.id && (
                                  <p className="text-[10px] text-muted-foreground mt-1">
                                    Parent: {doc.parent_id.substring(0, 8)}...
                                  </p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(doc.id, 'Job ID');
                            }}
                            title="Copy Job ID"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                        {/* Show parent indicator for child documents */}
                        {doc.parent_id && doc.parent_id !== doc.id && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Badge variant="outline" className="rounded-none text-[9px] px-1 py-0 bg-slate-50">
                              Child
                            </Badge>
                          </div>
                        )}
                        {/* Show child count for parent documents */}
                        {doc.child_count > 0 && doc.bill_type !== 'consolidated' && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Badge variant="outline" className="rounded-none text-[9px] px-1 py-0 bg-blue-50 text-blue-700">
                              {doc.child_count} children
                            </Badge>
                          </div>
                        )}
                      </td>
                      
                      {/* Document - Filename + Date/Time */}
                      <td className="p-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-mono text-sm font-medium truncate max-w-[180px]" title={doc.filename}>
                              {doc.filename}
                            </p>
                            {doc.billportal_exported && (
                              <Badge 
                                variant="outline" 
                                className="rounded-none text-[10px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200 shrink-0"
                                title="Converted to BillPortal format"
                              >
                                BP
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(doc.uploaded_at), 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                      </td>
                      
                      {/* Queue Tab specific columns */}
                      {activeBillType === 'queue' ? (
                        <>
                          {/* Created By */}
                          <td className="p-3 hidden md:table-cell">
                            <p className="text-sm">{doc.uploaded_by_name || '-'}</p>
                          </td>
                          
                          {/* Status */}
                          <td className="p-3">
                            <div className="flex flex-col gap-1">
                              {doc.status === 'processing' ? (
                                <Badge className="rounded-none bg-yellow-500 hover:bg-yellow-500 text-xs w-fit">
                                  <Clock className="h-3 w-3 mr-1 animate-spin" />Processing
                                </Badge>
                              ) : doc.status === 'failed' ? (
                                <Badge variant="destructive" className="rounded-none text-xs w-fit">
                                  <AlertCircle className="h-3 w-3 mr-1" />Failed
                                </Badge>
                              ) : doc.status === 'queued' ? (
                                <Badge variant="secondary" className="rounded-none text-xs w-fit">
                                  <Clock className="h-3 w-3 mr-1" />Queued
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="rounded-none text-xs w-fit">Uploaded</Badge>
                              )}
                            </div>
                          </td>
                          
                          {/* Actions for Queue */}
                          <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              {doc.status !== 'processing' && (
                                <Button
                                  data-testid={`reparse-document-${doc.id}`}
                                  size="sm"
                                  variant="outline"
                                  className="rounded-none h-7 px-2 text-xs"
                                  onClick={() => handleReparse(doc.id)}
                                  title="Re-parse document"
                                >
                                  <RefreshCw className="h-3 w-3 mr-1" />Reparse
                                </Button>
                              )}
                              <Button
                                data-testid={`download-document-${doc.id}`}
                                size="sm"
                                variant="outline"
                                className="rounded-none h-7 w-7 p-0"
                                onClick={() => handleDownload(doc.id, doc.filename)}
                                title="Download PDF"
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                              {doc.status === 'failed' && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="rounded-none h-7 px-2 text-xs"
                                    >
                                      <ArrowRight className="h-3 w-3 mr-1" />Move to
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleMoveToBills(doc.id, 'single')}>
                                      <FileText className="h-4 w-4 mr-2" />
                                      Single Bills
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleMoveToBills(doc.id, 'consolidated')}>
                                      <Layers className="h-4 w-4 mr-2" />
                                      Consolidated Bills
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                              {canDelete && (
                                <Button
                                  data-testid={`delete-document-${doc.id}`}
                                  size="sm"
                                  variant="ghost"
                                  className="rounded-none h-7 w-7 p-0 text-destructive hover:text-destructive"
                                  onClick={() => handleDelete(doc.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          {/* Details - Vendor + Commodity */}
                          <td className="p-3 hidden md:table-cell">
                            <div className="min-w-0">
                              <p className="text-sm truncate max-w-[120px]">{doc.vendor_name || '-'}</p>
                              <p className="text-xs text-muted-foreground">{doc.commodity || '-'}</p>
                            </div>
                          </td>
                          
                          {/* Accounts - Only for consolidated bills */}
                          {activeBillType === 'consolidated' && (
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="rounded-none bg-purple-50 text-purple-700 border-purple-200">
                                  <FileStack className="h-3 w-3 mr-1" />
                                  {doc.child_count || 0} accounts
                                </Badge>
                              </div>
                            </td>
                          )}
                          
                          
                          {/* Status - Review status */}
                          <td className="p-3">
                            <div className="flex flex-col gap-1">
                              {doc.status === 'completed' ? (
                                doc.review_status === 'approved' ? (
                                  <Badge className="rounded-none bg-blue-600 hover:bg-blue-600 text-xs w-fit">Approved</Badge>
                                ) : (
                                  <Badge variant="outline" className="rounded-none text-xs w-fit">Review</Badge>
                                )
                              ) : doc.status === 'processing' ? (
                                <Badge className="rounded-none bg-yellow-500 hover:bg-yellow-500 text-xs w-fit">
                                  <Clock className="h-3 w-3 mr-1 animate-spin" />Processing
                                </Badge>
                              ) : doc.status === 'failed' ? (
                                <Badge variant="destructive" className="rounded-none text-xs w-fit">Failed</Badge>
                              ) : (
                                <Badge variant="secondary" className="rounded-none text-xs w-fit">Uploaded</Badge>
                              )}
                            </div>
                          </td>
                          
                          {/* Assigned */}
                          <td className="p-3 hidden lg:table-cell" onClick={(e) => e.stopPropagation()}>
                            {doc.status === 'completed' ? (
                              <Select 
                                value={doc.assigned_to || '__unassigned__'} 
                                onValueChange={(value) => handleAssign(doc.id, value)}
                              >
                                <SelectTrigger className="rounded-none h-7 w-24 text-xs">
                                  <SelectValue placeholder="Assign" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                                  {accountUsers.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                          
                          {/* Last Updated */}
                          <td className="p-3 hidden md:table-cell">
                            {doc.last_updated_at ? (
                              <div className="flex items-center gap-1">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="text-xs">
                                        <p>{format(new Date(doc.last_updated_at), 'MMM d, h:mm a')}</p>
                                        <p className="text-muted-foreground">
                                          {doc.update_source === 'agent' ? 'by Agent' : doc.update_source === 'manual' ? 'Manual' : doc.update_source === 'reparse' ? 'Reparsed' : ''}
                                        </p>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Last updated: {format(new Date(doc.last_updated_at), 'MMM d, yyyy h:mm:ss a')}
                                      {doc.last_updated_by && ` by ${doc.last_updated_by}`}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="rounded-none h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(doc.bill_type === 'consolidated' ? `/consolidated-viewer/${doc.id}?tab=history` : `/viewer/${doc.id}?tab=history`);
                                        }}
                                      >
                                        <History className="h-3 w-3 text-muted-foreground" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>View history</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            ) : doc.processed_at ? (
                              <p className="text-xs text-muted-foreground">
                                {format(new Date(doc.processed_at), 'MMM d')}
                              </p>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                          
                          {/* Actions */}
                          <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              {(doc.status === 'completed' || doc.status === 'failed') && (
                                <>
                                  <Button
                                    data-testid={`view-document-${doc.id}`}
                                    size="sm"
                                    className="rounded-none font-bold uppercase h-7 text-xs px-2"
                                    onClick={() => navigate(doc.bill_type === 'consolidated' ? `/consolidated-viewer/${doc.id}` : `/viewer/${doc.id}`)}
                                  >
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    data-testid={`reparse-document-${doc.id}`}
                                    size="sm"
                                    variant="outline"
                                    className="rounded-none h-7 w-7 p-0"
                                    onClick={() => handleReparse(doc.id)}
                                    title="Re-parse document"
                                  >
                                    <RefreshCw className="h-3 w-3" />
                                  </Button>
                                </>
                              )}
                              {canDelete && (
                                <Button
                                  data-testid={`delete-document-${doc.id}`}
                                  size="sm"
                                  variant="ghost"
                                  className="rounded-none h-7 w-7 p-0"
                                  onClick={() => handleDelete(doc.id)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default BillJobs;
